import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCustomers, getCustomer, createCustomer, updateCustomer, toggleCustomerActive } from '../services/api';
import { formatCurrency, formatDate, StatusBadge } from '../utils/helpers';
import { UserCheck, Plus, Edit2, Power, ChevronRight, Building2, Phone, Mail, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { AppModal } from '../components/common/AppModal';

// ── LIST ──────────────────────────────────────────────────────────
export function CustomerList() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    getCustomers().then(r => setCustomers(r.data.customers || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleToggle = async (c, e) => {
    e.stopPropagation();
    try {
      await toggleCustomerActive(c.customer_id);
      toast.success(c.is_active ? 'Customer deactivated' : 'Customer activated');
      load();
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Customers</h2>
          <p className="text-sm text-gray-500 mt-0.5">Your buyers — onboarded as customers, no platform login required.</p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-primary"><Plus size={15}/> Onboard Customer</button>
      </div>

      {showModal && <CustomerFormModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }}/>}

      {loading ? (
        <div className="space-y-3 animate-pulse">{Array(3).fill(0).map((_, i) => <div key={i} className="h-24 bg-gray-100 rounded-xl"/>)}</div>
      ) : customers.length === 0 ? (
        <div className="card p-16 text-center">
          <UserCheck size={40} className="mx-auto text-gray-300 mb-3"/>
          <p className="text-gray-500 font-medium">No customers onboarded yet</p>
          <p className="text-gray-400 text-sm mt-1">Customers are your buyers. Add them here to link to Sales Orders and invoices.</p>
          <button onClick={() => setShowModal(true)} className="btn-primary mt-4 mx-auto"><Plus size={14}/> Onboard First Customer</button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr>{['Customer','Contact','GSTIN','City','POs','Status',''].map(h => <th key={h} className="table-th">{h}</th>)}</tr>
            </thead>
            <tbody>
              {customers.map(c => (
                <tr key={c.customer_id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/customers/${c.customer_id}`)}>
                  <td className="table-td">
                    <div className="font-medium">{c.name}</div>
                    {c.display_name && c.display_name !== c.name && <div className="text-xs text-gray-400">{c.display_name}</div>}
                  </td>
                  <td className="table-td text-sm text-gray-500">
                    <div>{c.contact_person || '—'}</div>
                    <div className="text-xs">{c.contact_email || c.email || ''}</div>
                  </td>
                  <td className="table-td font-mono text-xs text-gray-500">{c.gstin || '—'}</td>
                  <td className="table-td text-gray-500 text-sm">{[c.city, c.state].filter(Boolean).join(', ') || '—'}</td>
                  <td className="table-td text-center">
                    <span className="font-semibold">{c.po_count || 0}</span>
                    {c.total_po_value > 0 && <div className="text-xs text-gray-400">{formatCurrency(c.total_po_value, c.currency)}</div>}
                  </td>
                  <td className="table-td">
                    <span className={`badge ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="table-td"><ChevronRight size={15} className="text-gray-300"/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── DETAIL ────────────────────────────────────────────────────────
export function CustomerDetail() {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [editModal, setEditModal] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    getCustomer(id).then(r => setCustomer(r.data.customer)).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const handleToggle = async () => {
    try {
      await toggleCustomerActive(id);
      toast.success(customer.is_active ? 'Customer deactivated' : 'Customer activated');
      load();
    } catch (err) { toast.error('Failed'); }
  };

  if (loading) return <div className="animate-pulse h-96 bg-gray-100 rounded-xl"/>;
  if (!customer) return <div className="card p-12 text-center text-gray-400">Customer not found</div>;

  const infoBlocks = [
    { label: 'GSTIN',           value: customer.gstin },
    { label: 'PAN',             value: customer.pan },
    { label: 'Email',           value: customer.email },
    { label: 'Phone',           value: customer.phone },
    { label: 'Contact Person',  value: customer.contact_person },
    { label: 'Contact Email',   value: customer.contact_email },
    { label: 'Contact Phone',   value: customer.contact_phone },
    { label: 'Website',         value: customer.website },
    { label: 'Currency',        value: customer.currency },
    { label: 'Payment Terms',   value: customer.payment_terms_days ? `${customer.payment_terms_days} days` : null },
    { label: 'Credit Limit',    value: customer.credit_limit ? formatCurrency(customer.credit_limit, customer.currency) : 'Unlimited' },
    { label: 'Customer Since',  value: formatDate(customer.created_at) },
  ];

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button onClick={() => navigate('/customers')} className="hover:text-primary-600">Customers</button>
        <ChevronRight size={14}/>
        <span className="text-gray-900 font-medium">{customer.name}</span>
      </div>

      {/* Header card */}
      <div className="card p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold">{customer.name}</h1>
              <span className={`badge ${customer.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                {customer.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            {customer.display_name && customer.display_name !== customer.name &&
              <p className="text-gray-500 text-sm">{customer.display_name}</p>}
            {[customer.city, customer.state, customer.country].filter(Boolean).length > 0 &&
              <p className="text-gray-400 text-sm mt-1">
                <Building2 size={12} className="inline mr-1"/>
                {[customer.city, customer.state, customer.country].filter(Boolean).join(', ')}
              </p>}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditModal(true)} className="btn-secondary">
              <Edit2 size={14}/> Edit
            </button>
            <button onClick={handleToggle}
              className={customer.is_active ? 'btn-secondary text-red-600 border-red-200' : 'btn-primary bg-green-600 hover:bg-green-700'}>
              <Power size={14}/> {customer.is_active ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </div>

        {customer.address && (
          <div className="mt-4 text-sm text-gray-500 flex items-start gap-1.5">
            <Building2 size={14} className="mt-0.5 flex-shrink-0"/>
            <span>{customer.address}{customer.pincode ? ` — ${customer.pincode}` : ''}</span>
          </div>
        )}

        {/* Info grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-gray-100">
          {infoBlocks.filter(b => b.value).map(b => (
            <div key={b.label}>
              <div className="text-xs text-gray-400">{b.label}</div>
              <div className="text-sm font-medium mt-0.5">{b.value}</div>
            </div>
          ))}
        </div>

        {customer.notes && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
            <strong>Notes:</strong> {customer.notes}
          </div>
        )}
      </div>

      {/* Purchase Orders */}
      <div className="card">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Purchase Orders</h3>
          <button onClick={() => navigate('/purchase-orders')} className="btn-secondary text-sm">
            <Plus size={13}/> New Sales Order
          </button>
        </div>
        {!customer.purchase_orders?.length ? (
          <div className="px-5 py-10 text-center text-gray-400 text-sm">
            <FileText size={28} className="mx-auto mb-2 opacity-30"/>
            No purchase orders yet
          </div>
        ) : (
          <table className="w-full">
            <thead><tr>{['SO Number','Title','Value','Status','Date'].map(h => <th key={h} className="table-th">{h}</th>)}</tr></thead>
            <tbody>
              {customer.purchase_orders.map(po => (
                <tr key={po.po_id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/purchase-orders/${po.po_id}`)}>
                  <td className="table-td font-mono text-primary-600 text-sm">{po.po_number}</td>
                  <td className="table-td">{po.title}</td>
                  <td className="table-td font-semibold">{formatCurrency(po.po_value, po.currency)}</td>
                  <td className="table-td"><StatusBadge status={po.status}/></td>
                  <td className="table-td text-gray-500">{formatDate(po.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editModal && (
        <CustomerFormModal
          customer={customer}
          onClose={() => setEditModal(false)}
          onSaved={() => { setEditModal(false); load(); }}
        />
      )}
    </div>
  );
}

// ── FORM MODAL ────────────────────────────────────────────────────
function CustomerFormModal({ customer, onClose, onSaved }) {
  const isEdit = !!customer;
  const [form, setForm]   = useState(customer || { currency: 'INR', payment_terms_days: 30, country: 'India' });
  const [orgs, setOrgs]   = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    import('../services/api').then(({ getOrganisations }) => {
      getOrganisations().then(r => {
        const orgList = r.data.organisations || [];
        setOrgs(orgList);
        if (!isEdit && orgList.length > 0) setForm(f => ({ ...f, seller_org_id: orgList[0].org_id }));
      });
    });
  }, [isEdit]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name)          return toast.error('Customer name is required');
    if (!form.seller_org_id) return toast.error('Please select your organisation');
    setSaving(true);
    try {
      if (isEdit) {
        await updateCustomer(customer.customer_id, form);
        toast.success('Customer updated');
      } else {
        await createCustomer(form);
        toast.success('Customer onboarded successfully');
      }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <AppModal
      title={isEdit ? `Edit: ${customer.name}` : 'Onboard Customer'}
      onClose={onClose}
      maxWidth={672}
    >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Core info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label">Company / Customer Name <span className="text-red-500">*</span></label>
              <input className="input" value={form.name || ''} onChange={e => set('name', e.target.value)} required autoFocus/>
            </div>
            <div>
              <label className="label">Display Name</label>
              <input className="input" value={form.display_name || ''} onChange={e => set('display_name', e.target.value)} placeholder="Short name used in lists"/>
            </div>
            <div>
              <label className="label">Your Organisation (Seller) <span className="text-red-500">*</span></label>
              <select className="select" value={form.seller_org_id || ''} onChange={e => set('seller_org_id', e.target.value)} required>
                <option value="">— Select —</option>
                {orgs.map(o => <option key={o.org_id} value={o.org_id}>{o.name}</option>)}
              </select>
            </div>
          </div>

          <hr className="border-gray-100"/>

          {/* Tax / legal */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Tax & Legal</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="label">GSTIN</label><input className="input" value={form.gstin || ''} onChange={e => set('gstin', e.target.value)} placeholder="22AAAAA0000A1Z5"/></div>
            <div><label className="label">PAN</label><input className="input" value={form.pan || ''} onChange={e => set('pan', e.target.value)} placeholder="AAAAA9999A"/></div>
          </div>

          <hr className="border-gray-100"/>

          {/* Contact */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Contact Details</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="label">Company Email</label><input type="email" className="input" value={form.email || ''} onChange={e => set('email', e.target.value)}/></div>
            <div><label className="label">Company Phone</label><input className="input" value={form.phone || ''} onChange={e => set('phone', e.target.value)}/></div>
            <div><label className="label">Contact Person</label><input className="input" value={form.contact_person || ''} onChange={e => set('contact_person', e.target.value)}/></div>
            <div><label className="label">Contact Email</label><input type="email" className="input" value={form.contact_email || ''} onChange={e => set('contact_email', e.target.value)}/></div>
            <div><label className="label">Contact Phone</label><input className="input" value={form.contact_phone || ''} onChange={e => set('contact_phone', e.target.value)}/></div>
            <div><label className="label">Website</label><input className="input" value={form.website || ''} onChange={e => set('website', e.target.value)} placeholder="https://"/></div>
          </div>

          <hr className="border-gray-100"/>

          {/* Address */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Address</p>
          <div>
            <label className="label">Street Address</label>
            <textarea className="input" rows={2} value={form.address || ''} onChange={e => set('address', e.target.value)}/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><label className="label">City</label><input className="input" value={form.city || ''} onChange={e => set('city', e.target.value)}/></div>
            <div><label className="label">State</label><input className="input" value={form.state || ''} onChange={e => set('state', e.target.value)}/></div>
            <div><label className="label">Pincode</label><input className="input" value={form.pincode || ''} onChange={e => set('pincode', e.target.value)}/></div>
            <div><label className="label">Country</label><input className="input" value={form.country || 'India'} onChange={e => set('country', e.target.value)}/></div>
          </div>

          <hr className="border-gray-100"/>

          {/* Commercial */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Commercial Terms</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Currency</label>
              <select className="select" value={form.currency || 'INR'} onChange={e => set('currency', e.target.value)}>
                {['INR','USD','EUR','GBP','AED','SGD'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><label className="label">Payment Terms (days)</label><input type="number" className="input" value={form.payment_terms_days || 30} onChange={e => set('payment_terms_days', parseInt(e.target.value) || 30)}/></div>
            <div><label className="label">Credit Limit</label><input type="number" step="0.01" className="input" value={form.credit_limit || ''} onChange={e => set('credit_limit', e.target.value || null)} placeholder="Blank = unlimited"/></div>
          </div>

          <div>
            <label className="label">Internal Notes</label>
            <textarea className="input" rows={2} value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Internal notes about this customer"/>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Onboard Customer'}
            </button>
          </div>
        </form>
    </AppModal>
  );
}
